import type { OperationLine, OperationLineSide } from '@/api/models';

/**
 * What changed between two versions of a recipe.
 *
 * ## Why the diff is here and not on the server
 *
 * The history endpoint sends every version's **complete line set**, and this
 * computes the delta. A server-side diff would settle once and for all what
 * "changed" means — and the answer depends on how it is being read. Here it can
 * be changed, tested and argued with in one pure function.
 *
 * ## How a line is matched across versions
 *
 * By its **role label first, pair group second**, and that order is the whole
 * design.
 *
 * `pairGroup` is renumbered dense on every save: delete the middle pair of three
 * and the third becomes the second. Matching on it alone would report a deleted
 * pair as "pair 2 changed everything and pair 3 was removed", which is true of
 * the numbers and false about the recipe.
 *
 * `roleLabel` is what a line *is* — `expense`, `funding`, `vat` — and it survives
 * renumbering, because it describes the job rather than the position. It is
 * optional, though, and it is not unique, so it cannot be the only key: a pair
 * falls back to its group number when either of its lines is unlabelled.
 *
 * The consequence worth knowing: **renaming a role label reads as one pair
 * removed and another added.** That is honest — the author changed what the
 * pair claims to be — and the alternative (falling back to position when the
 * labels do not match) would silently pair up two legs that have nothing to do
 * with each other.
 */

/** A version's lines regrouped into the pairs the editor works in. */
export type DiffPair = {
    /** The pair's identity across versions. See the file header. */
    key: string;
    pairGroup: number;
    debit: OperationLine | null;
    credit: OperationLine | null;
};

/** Which fields of one line differ between two versions. */
export type LineFieldChange =
    'account' | 'amountSource' | 'slotLabel' | 'filter';

/** One side of a changed pair, with what changed on it. */
export type DiffLine = {
    side: OperationLineSide;
    before: OperationLine | null;
    after: OperationLine | null;
    /** Empty when this side is untouched. */
    changes: LineFieldChange[];
};

export type DiffPairStatus = 'UNCHANGED' | 'CHANGED' | 'ADDED' | 'REMOVED';

export type PairDiff = {
    key: string;
    status: DiffPairStatus;
    /** The pair group to name it by: the newer one where there is one. */
    pairGroup: number;
    lines: DiffLine[];
};

export type VersionDiff = {
    pairs: PairDiff[];
    /** True when nothing at all differs — a republish with no edits. */
    isIdentical: boolean;
};

/** Group a flat line list into pairs, in the order an entry is written. */
export const toDiffPairs = (lines: readonly OperationLine[]): DiffPair[] => {
    const groups = [...new Set(lines.map((line) => line.pairGroup))].sort(
        (a, b) => a - b,
    );
    return groups.map((pairGroup) => {
        const debit =
            lines.find(
                (l) => l.pairGroup === pairGroup && l.side === 'DEBIT',
            ) ?? null;
        const credit =
            lines.find(
                (l) => l.pairGroup === pairGroup && l.side === 'CREDIT',
            ) ?? null;
        return {
            key: pairKey(pairGroup, debit, credit),
            pairGroup,
            debit,
            credit,
        };
    });
};

/**
 * A pair's identity: its two role labels when it has both, otherwise its group
 * number.
 *
 * The `#` prefix on the positional form is not decoration — it keeps a pair
 * labelled `1/2` from colliding with the pair that happens to sit at group 1
 * with an unlabelled sibling.
 */
const pairKey = (
    pairGroup: number,
    debit: OperationLine | null,
    credit: OperationLine | null,
): string =>
    debit?.roleLabel && credit?.roleLabel
        ? `${debit.roleLabel}/${credit.roleLabel}`
        : `#${pairGroup}`;

/** Whether a line is a slot (the recorder chooses) rather than a fixed account. */
const isSlot = (line: OperationLine): boolean => line.accountId === null;

/**
 * What differs between one side's two versions.
 *
 * A **fixed↔slot flip** reports as an `account` change: from the reader's point
 * of view the line stopped naming an account and started asking for one, which
 * is the same kind of fact as swapping one account for another.
 */
const lineChanges = (
    before: OperationLine,
    after: OperationLine,
): LineFieldChange[] => {
    const changes: LineFieldChange[] = [];
    if (
        before.accountId !== after.accountId ||
        isSlot(before) !== isSlot(after)
    ) {
        changes.push('account');
    }
    if (before.amountSource !== after.amountSource) {
        changes.push('amountSource');
    }
    if (
        before.slotLabelKa !== after.slotLabelKa ||
        before.slotLabelEn !== after.slotLabelEn
    ) {
        changes.push('slotLabel');
    }
    if (before.accountFilter.join(',') !== after.accountFilter.join(',')) {
        changes.push('filter');
    }
    return changes;
};

const sideOf = (
    pair: DiffPair,
    side: OperationLineSide,
): OperationLine | null => (side === 'DEBIT' ? pair.debit : pair.credit);

/**
 * Diff one version's lines against its predecessor's.
 *
 * `previous` is `null` for the **first** version, which has nothing to be
 * compared against: every pair reports as `ADDED`, which is what a first recipe
 * is. Callers render that as "the original", not as a change.
 *
 * The output is ordered by the **newer** version's pairs, with removed ones
 * appended: a reader scanning a diff is looking at what the recipe is now, and
 * what is gone belongs at the end rather than interleaved by a position it no
 * longer occupies.
 */
export const diffVersions = (
    previous: readonly OperationLine[] | null,
    current: readonly OperationLine[],
): VersionDiff => {
    const currentPairs = toDiffPairs(current);
    const previousPairs = previous === null ? [] : toDiffPairs(previous);
    const matched = new Set<string>();

    const pairs: PairDiff[] = currentPairs.map((pair) => {
        const before = previousPairs.find((p) => p.key === pair.key) ?? null;
        if (before) {
            matched.add(before.key);
        }
        if (!before) {
            return {
                key: pair.key,
                status: 'ADDED',
                pairGroup: pair.pairGroup,
                lines: (['DEBIT', 'CREDIT'] as const).map((side) => ({
                    side,
                    before: null,
                    after: sideOf(pair, side),
                    changes: [],
                })),
            };
        }

        const lines: DiffLine[] = (['DEBIT', 'CREDIT'] as const).map((side) => {
            const from = sideOf(before, side);
            const to = sideOf(pair, side);
            return {
                side,
                before: from,
                after: to,
                // A side that exists on only one of the two versions is a change
                // in itself — a pair the server would refuse, but one the editor
                // can produce and this view has to be able to show.
                changes:
                    from && to
                        ? lineChanges(from, to)
                        : from || to
                          ? ['account']
                          : [],
            };
        });

        return {
            key: pair.key,
            status: lines.some((line) => line.changes.length > 0)
                ? 'CHANGED'
                : 'UNCHANGED',
            pairGroup: pair.pairGroup,
            lines,
        };
    });

    const removed: PairDiff[] = previousPairs
        .filter((pair) => !matched.has(pair.key))
        .map((pair) => ({
            key: pair.key,
            status: 'REMOVED',
            pairGroup: pair.pairGroup,
            lines: (['DEBIT', 'CREDIT'] as const).map((side) => ({
                side,
                before: sideOf(pair, side),
                after: null,
                changes: [],
            })),
        }));

    const all = [...pairs, ...removed];
    return {
        pairs: all,
        isIdentical: all.every((pair) => pair.status === 'UNCHANGED'),
    };
};
